/* Phones (<= 749 px) get a lower rendition of Shopify-hosted decorative videos. Liquid exposes only the top MP4
   (1080p: 2-28 MB per clip); Shopify encodes every upload on one bitrate ladder under the same file id, so the
   smaller file's name is known. If that file fails to load, the video falls back to the original. */
window.cfPhoneVideoSrc = (() => {
  const phone = matchMedia('(max-width: 749px)');
  const rung = /(?:HD|SD)-(\d+)p-[\d.]+Mbps/;
  const down = { // one rung down, same file id
    'HD-1080p-7.2Mbps': 'HD-720p-4.5Mbps', 'HD-1080p-4.8Mbps': 'HD-720p-3.0Mbps', 'HD-1080p-3.3Mbps': 'HD-720p-2.1Mbps', 'HD-1080p-2.5Mbps': 'HD-720p-1.6Mbps',
    'HD-720p-4.5Mbps': 'SD-480p-1.5Mbps', 'HD-720p-3.0Mbps': 'SD-480p-1.2Mbps', 'HD-720p-2.1Mbps': 'SD-480p-1.0Mbps', 'HD-720p-1.6Mbps': 'SD-480p-0.9Mbps'
  };
  return (source, maxHeight) => {
    const original = source.dataset.src;
    if (!phone.matches || !original.includes('/videos/c/vp/')) return original;
    let url = original, label;
    while ((label = url.match(rung)) && +label[1] > maxHeight && down[label[0]]) url = url.replace(label[0], down[label[0]]);
    if (url !== original) source.addEventListener('error', () => {
      const video = source.parentElement;
      const resume = !video.paused;
      source.src = original;
      video.load();
      if (resume) video.play()?.catch(() => {});
    }, { once: true });
    return url;
  };
})();

/* Load decorative videos only while visible; keep posters for reduced motion/data. */
(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  // data-cf-poster-only keeps the poster everywhere; data-cf-poster-only-phone only up to 749 px.
  const phone = matchMedia('(max-width: 749px)');
  const posterOnly = video => video.hasAttribute('data-cf-poster-only') || (video.hasAttribute('data-cf-poster-only-phone') && phone.matches);
  const videos = new Set();
  // Videos marked data-cf-after-load (the footer) wait for the load event plus an idle moment.
  let pageSettled = false;
  const settle = () => { pageSettled = true; videos.forEach(sync); };
  const afterIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(settle, { timeout: 3000 }) : setTimeout(settle, 1000));
  if (document.readyState === 'complete') afterIdle();
  else window.addEventListener('load', afterIdle, { once: true });
  setTimeout(settle, 8000); // fallback if a third-party resource stalls the load event
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      entry.target.dataset.cfVisible = entry.isIntersecting ? 'true' : 'false';
      sync(entry.target);
    });
  }, { threshold: 0.01 }) : null;
  function sync(video) {
    if (document.hidden || reducedMotion.matches || navigator.connection?.saveData || posterOnly(video) || video.dataset.cfVisible !== 'true') {
      video.pause();
      return;
    }
    if (!video.dataset.cfLoaded) {
      if (video.hasAttribute('data-cf-after-load') && !pageSettled) return;
      // Use the first source whose media query matches (a small file for phones) and drop the others.
      const sources = Array.from(video.querySelectorAll('source[data-src]'));
      const chosen = sources.find(source => !source.media || matchMedia(source.media).matches) || sources[sources.length - 1];
      sources.forEach(source => {
        if (source !== chosen) { source.remove(); return; }
        source.src = window.cfPhoneVideoSrc(source, 720); // product cards: at most 720p on phones
        source.removeAttribute('data-src');
        source.removeAttribute('media');
      });
      video.dataset.cfLoaded = 'true';
      video.load();
    }
    video.play()?.catch(() => {}); // Autoplay denied: the existing poster stays visible.
  }
  function init(root) {
    root.querySelectorAll('video[data-cf-viewport-video]').forEach(video => {
      if (videos.has(video)) return;
      videos.add(video);
      video.muted = true;
      video.defaultMuted = true;
      if (video.classList.contains('my-card__video-override')) {
        video.style.zIndex = '1';
        const photo = video.parentElement.querySelector('img.my-card__image--primary');
        if (photo) photo.style.zIndex = '0';
        video.addEventListener('error', () => { video.style.display = 'none'; if (photo) photo.style.opacity = '1'; });
      }
      observer?.observe(video); // Unsupported browsers retain still images without downloading.
    });
  }
  init(document);
  document.addEventListener('visibilitychange', () => videos.forEach(sync));
  reducedMotion.addEventListener('change', () => videos.forEach(sync));
  phone.addEventListener('change', () => videos.forEach(sync));
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    videos.forEach(video => { if (event.target.contains(video)) { observer?.unobserve(video); video.pause(); videos.delete(video); } });
  });
})();

/* Two or more decorative videos decoding at once drop page scrolling to ~30 fps (measured 2026-10-28),
   so of the muted loops that sections want playing, only the most visible one plays; the rest keep their frame. */
(() => {
  if (!('IntersectionObserver' in window)) return;
  const decorative = video => video.muted && video.loop && !video.controls && !video.classList.contains('cf-turnable-video');
  const wanted = new Set(); // videos their own section code has started
  const area = new WeakMap(); // visible pixels, clipped by sliders and the viewport
  const own = new WeakSet(); // play/pause events this script caused
  let queued = false;
  const visibleArea = rect => Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0)) * Math.max(0, Math.min(rect.bottom, innerHeight) - Math.max(rect.top, 0));
  const choose = () => {
    queued = false;
    let winner = null;
    wanted.forEach(video => { if ((area.get(video) || 0) > (winner ? area.get(winner) : 0)) winner = video; });
    wanted.forEach(video => {
      if (video === winner && video.paused) { own.add(video); video.play()?.catch(() => own.delete(video)); }
      else if (video !== winner && !video.paused) { own.add(video); video.pause(); }
    });
  };
  const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(choose); } };
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => area.set(entry.target, entry.intersectionRect.width * entry.intersectionRect.height));
    schedule();
  }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] });
  const track = video => {
    if (!area.has(video)) { area.set(video, visibleArea(video.getBoundingClientRect())); observer.observe(video); }
    wanted.add(video);
    schedule();
  };
  document.addEventListener('play', event => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || !decorative(video)) return;
    if (own.has(video)) { own.delete(video); return; }
    track(video);
  }, true);
  document.addEventListener('pause', event => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || !wanted.has(video)) return;
    if (own.has(video)) { own.delete(video); return; }
    wanted.delete(video);
    schedule();
  }, true);
  // Autoplay videos may have started before this deferred script ran.
  document.querySelectorAll('video').forEach(video => { if (!video.paused && decorative(video)) track(video); });
})();
